import { error as logErrorToConsole } from '@ms/yammer-console-logging';
import { getTelemetryClientConfig } from '@ms/yammer-telemetry-store';

import type {
  AnalyticsV2Event,
  AnalyticsV2EventInput,
  LogEventInput,
  LogEventWithOptions,
  ReportAnalyticsEventsInput,
  ReportLogEventsInput,
} from '../events';

import { isKeepaliveRequestBodyWithinLimit } from './getLogEventsWithinKeepaliveRequestBodyLimit';
import { loadProtobufRequestFunctions } from './protobuf/request';
import type { DeliveryLifecycleEventInputType } from './reportDeliveryLifecycleInfo';
import {
  createDeliveryLifecycleCorrelationId,
  getDeliveryLifecycleErrorType,
  getDeliveryLifecycleOutcomeForResponseStatus,
  getReportableDeliveryLifecycleLogEventCount,
  reportDeliveryLifecycleInfo,
} from './reportDeliveryLifecycleInfo';
import type { GetEventHeadersWithAuthentication, GetEventHeadersWithoutAuthentication } from './reportHeaders';
import {
  getEventHeadersForPageHide,
  getEventHeadersWithAuthentication,
  getEventHeadersWithoutAuthentication,
} from './reportHeaders';

const UnknownClientId = 'UNKNOWN';
const LogEventApiPath = '/api/v1/yamalytics/webui';
const AnalyticsEventApiPath = '/api/v2/events';
const AnalyticsV2EventApiPath = '/api/v3/events';

const createJsonRequestBody = (events: LogEventInput[] | ReportAnalyticsEventsInput) => JSON.stringify(events);

const createRequestOptions = (
  headers: Headers,
  events: LogEventInput[] | ReportAnalyticsEventsInput,
  keepalive = false
) => ({
  headers,
  method: 'POST',
  body: createJsonRequestBody(events),
  ...(keepalive && { keepalive }),
});

const createAnalyticsV2RequestOptions = async (headers: Headers, events: AnalyticsV2Event[]) => {
  const { getProtobufEventRequestBody } = await loadProtobufRequestFunctions();

  return {
    headers,
    method: 'POST',
    body: getProtobufEventRequestBody(events),
  };
};

const createUrl = (host: string, path: string) => `${host}${path}`;

const createHeaders = (headers: Record<string, string>) =>
  new Headers({
    ...headers,
    accept: 'application/json',
    'content-type': 'application/json',
  });

class BadFetchResponseError extends Error {
  constructor(response: Response, details: string) {
    super(`Fetch failed for ${response.url} with status ${response.statusText} with details ${details}`);
  }
}

const getBadFetchResponseDetails = async (response: Response) => {
  try {
    return await response.text();
  } catch (error) {
    return `Failed to read response details: ${String(error)}`;
  }
};

const throwErrorOnBadFetchResponse = async (response: Response) => {
  if (!response.ok) {
    throw new BadFetchResponseError(response, await getBadFetchResponseDetails(response));
  }
};

const logTelemetryReportingFailure = (error: unknown) => {
  logErrorToConsole(`Telemetry reporting failed. Details: ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`);
};

const reportAttemptedDeliveryLifecycleInfo = (eventInputType: DeliveryLifecycleEventInputType, resultCount: number) => {
  const correlationId = createDeliveryLifecycleCorrelationId();
  reportDeliveryLifecycleInfo({
    correlationId,
    eventInputType,
    outcome: 'attempted',
    resultCount,
  });

  return correlationId;
};

const executeFetchRequest = async (
  url: string,
  requestOptions: RequestInit,
  eventInputType: DeliveryLifecycleEventInputType,
  shouldReportDeliveryLifecycle: boolean,
  resultCount: number,
  correlationId?: string
) => {
  const response = await fetch(url, requestOptions);
  if (shouldReportDeliveryLifecycle) {
    reportDeliveryLifecycleInfo({
      correlationId,
      eventInputType,
      outcome: response.ok ? 'sent' : getDeliveryLifecycleOutcomeForResponseStatus(response.status),
      responseCode: response.status,
      resultCount,
    });
  }

  await throwErrorOnBadFetchResponse(response);
};

type ReportTelemetryEventsToServer = (
  requestOptions: RequestInit,
  apiPath: string,
  eventInputType: DeliveryLifecycleEventInputType,
  shouldReportDeliveryLifecycle: boolean,
  resultCount: number,
  correlationId?: string
) => Promise<void>;
const reportTelemetryEventsToServer: ReportTelemetryEventsToServer = async (
  requestOptions,
  apiPath,
  eventInputType,
  shouldReportDeliveryLifecycle,
  resultCount,
  correlationId
) => {
  const clientConfig = getTelemetryClientConfig();
  if (!clientConfig?.telemetryHost) {
    if (shouldReportDeliveryLifecycle) {
      reportDeliveryLifecycleInfo({
        correlationId,
        eventInputType,
        outcome: 'telemetry_host_unavailable',
        resultCount,
      });
    }

    return;
  }

  const url = createUrl(clientConfig.telemetryHost, apiPath);
  try {
    await executeFetchRequest(
      url,
      requestOptions,
      eventInputType,
      shouldReportDeliveryLifecycle,
      resultCount,
      correlationId
    );
  } catch (error) {
    if (shouldReportDeliveryLifecycle && !(error instanceof BadFetchResponseError)) {
      reportDeliveryLifecycleInfo({
        correlationId,
        eventInputType,
        outcome: 'fetch_failed',
        errorType: getDeliveryLifecycleErrorType(error),
        resultCount,
      });
    }

    throw error;
  }
};

type ReportLogEventsNow = (logEventsInput: ReportLogEventsInput) => Promise<void>;
export const reportLogEventsNow: ReportLogEventsNow = async (logEventsInput) => {
  const clientConfig = getTelemetryClientConfig();
  const allEvents: LogEventWithOptions[] = [
    ...logEventsInput.errorEvents,
    ...logEventsInput.performanceEvents,
    ...logEventsInput.infoEvents,
  ];

  if (clientConfig?.telemetryClientId === UnknownClientId) {
    logErrorToConsole('Unexpected telemetryClientId = UNKNOWN');
    const reportableEventCount = getReportableDeliveryLifecycleLogEventCount(allEvents.map(({ event }) => event));
    if (reportableEventCount > 0) {
      const correlationId = reportAttemptedDeliveryLifecycleInfo('Log', reportableEventCount);
      reportDeliveryLifecycleInfo({
        correlationId,
        eventInputType: 'Log',
        outcome: 'invalid_client_id',
        resultCount: reportableEventCount,
      });
    }

    return;
  }

  try {
    await reportLogEventsToServer(allEvents);
  } catch (err) {
    logTelemetryReportingFailure(err);
  }
};

type ReportLogEventsWithKeepalive = (logEventInputs: LogEventInput[]) => boolean;
export const reportLogEventsWithKeepalive: ReportLogEventsWithKeepalive = (logEventInputs) => {
  const clientConfig = getTelemetryClientConfig();

  if (clientConfig?.telemetryClientId === UnknownClientId) {
    logErrorToConsole('Unexpected telemetryClientId = UNKNOWN');

    return false;
  }

  if (!clientConfig?.telemetryHost) {
    return false;
  }

  if (logEventInputs.length === 0 || !isKeepaliveRequestBodyWithinLimit(logEventInputs)) {
    return false;
  }

  void reportTelemetryEventsToServer(
    createRequestOptions(createHeaders(getEventHeadersForPageHide()), logEventInputs, true),
    LogEventApiPath,
    'Log',
    false,
    logEventInputs.length
  ).catch((error: unknown) => {
    logTelemetryReportingFailure(error);
  });

  return true;
};

type ReportLogEventWithKeepalive = (logEventInput: LogEventInput) => boolean;
export const reportLogEventWithKeepalive: ReportLogEventWithKeepalive = (logEventInput) =>
  reportLogEventsWithKeepalive([logEventInput]);

type ReportAnalyticsEventsNow = (analyticsEventsInput: ReportAnalyticsEventsInput) => Promise<void>;
export const reportAnalyticsEventsNow: ReportAnalyticsEventsNow = (analyticsEventsInput) =>
  reportTelemetryEventsNow({ events: analyticsEventsInput, eventInputType: 'Analytics' });

type ReportAnalyticsV2EventsNow = (analyticsEventsInput: AnalyticsV2EventInput[]) => Promise<void>;
export const reportAnalyticsV2EventsNow: ReportAnalyticsV2EventsNow = (analyticsEventsInput) =>
  reportTelemetryEventsNow({ events: analyticsEventsInput, eventInputType: 'AnalyticsV2' });

const groupLogEventsBySkipAuthentication = (eventsWithOptions: LogEventWithOptions[]) =>
  eventsWithOptions.reduce<Record<string, LogEventInput[]>>(
    (acc, { event, options }) => {
      const key = options?.skipAuthentication ? 'true' : 'false';
      acc[key].push(event);

      return acc;
    },
    { false: [], true: [] }
  );

type GetLogEventHeaders = GetEventHeadersWithAuthentication | GetEventHeadersWithoutAuthentication;
const reportLogEventGroupToServer = async (events: LogEventInput[], getLogEventHeaders: GetLogEventHeaders) => {
  if (events.length === 0) {
    return;
  }

  const reportableEventCount = getReportableDeliveryLifecycleLogEventCount(events);
  const correlationId =
    reportableEventCount > 0 ? reportAttemptedDeliveryLifecycleInfo('Log', reportableEventCount) : undefined;

  await reportTelemetryEventsToServer(
    createRequestOptions(createHeaders(await getLogEventHeaders()), events),
    LogEventApiPath,
    'Log',
    correlationId != null,
    reportableEventCount,
    correlationId
  );
};

const reportLogEventsToServer = async (eventsWithOptions: LogEventWithOptions[]) => {
  const groupedEvents = groupLogEventsBySkipAuthentication(eventsWithOptions);

  await Promise.all([
    reportLogEventGroupToServer(groupedEvents.false, getEventHeadersWithAuthentication),
    reportLogEventGroupToServer(groupedEvents.true, getEventHeadersWithoutAuthentication),
  ]);
};

interface ReportAnalyticsEventsNowProps {
  readonly eventInputType: 'Analytics';
  readonly events: ReportAnalyticsEventsInput;
}

interface ReportAnalyticsV2EventsNowProps {
  readonly eventInputType: 'AnalyticsV2';
  readonly events: AnalyticsV2EventInput[];
}

type ReportTelemetryEventsNowProps = ReportAnalyticsEventsNowProps | ReportAnalyticsV2EventsNowProps;

type ReportTelemetryEventsNow = (props: ReportTelemetryEventsNowProps) => Promise<void>;
const reportTelemetryEventsNow: ReportTelemetryEventsNow = async (props) => {
  const clientConfig = getTelemetryClientConfig();
  const resultCount = props.eventInputType === 'Analytics' ? (props.events.events?.length ?? 0) : props.events.length;

  if (clientConfig?.telemetryClientId === UnknownClientId) {
    logErrorToConsole('Unexpected telemetryClientId = UNKNOWN');
    const correlationId = reportAttemptedDeliveryLifecycleInfo(props.eventInputType, resultCount);
    reportDeliveryLifecycleInfo({
      correlationId,
      eventInputType: props.eventInputType,
      outcome: 'invalid_client_id',
      resultCount,
    });

    return;
  }

  try {
    if (props.eventInputType === 'Analytics') {
      const fetchHeaders = createHeaders(await getEventHeadersWithAuthentication());
      const requestOptions = createRequestOptions(fetchHeaders, props.events);
      const correlationId = reportAttemptedDeliveryLifecycleInfo('Analytics', resultCount);

      await reportTelemetryEventsToServer(
        requestOptions,
        AnalyticsEventApiPath,
        'Analytics',
        true,
        resultCount,
        correlationId
      );
    } else {
      const fetchHeaders = createHeaders(await getEventHeadersWithAuthentication());
      const requestOptions = await createAnalyticsV2RequestOptions(fetchHeaders, props.events);
      const correlationId = reportAttemptedDeliveryLifecycleInfo('AnalyticsV2', resultCount);

      await reportTelemetryEventsToServer(
        requestOptions,
        AnalyticsV2EventApiPath,
        'AnalyticsV2',
        true,
        resultCount,
        correlationId
      );
    }
  } catch (err) {
    logTelemetryReportingFailure(err);
  }
};
